// FIX-CCS-OUTBOX (spec §7.1, §7.3) — the phone's photo outbox: IndexedDB database 'agxl-outbox',
// object store 'photoOutbox' (one item per photo, key = photo id) and a small 'meta' store that holds
// the runner lock and the persistent-storage record.
//
// Blobs are stored directly (structured clone; MDN: "Blobs and files can be stored"). The runner
// lock is a record in 'meta' written inside a readwrite transaction: IndexedDB serialises
// overlapping readwrite transactions on the same store across tabs, so check-and-set is atomic.
// The logout path never touches this database (R6: a forced logout does not clear the outbox).

import { openDB, type IDBPDatabase } from 'idb';
import type { OutboxItem } from './types';

export const OUTBOX_DB_NAME = 'agxl-outbox';
export const OUTBOX_DB_VERSION = 1;
export const OUTBOX_STORE = 'photoOutbox';
const META_STORE = 'meta';
const LOCK_KEY = 'runner_lock';

export interface RunnerLock {
  holder: string;
  expires_at: number;
}

export interface OutboxStore {
  put(item: OutboxItem): Promise<void>;
  get(photoId: string): Promise<OutboxItem | undefined>;
  delete(photoId: string): Promise<void>;
  all(): Promise<OutboxItem[]>;
  /** Read-modify-write one item in a single transaction. Returns the new item, or undefined if it is gone. */
  update(photoId: string, patch: Partial<OutboxItem>): Promise<OutboxItem | undefined>;
  /** Take the runner lock if it is free, expired, or already ours. */
  acquireLock(holder: string, ttlMs: number, nowMs?: number): Promise<boolean>;
  releaseLock(holder: string): Promise<void>;
  getMeta<T>(key: string): Promise<T | undefined>;
  setMeta<T>(key: string, value: T): Promise<void>;
}

type Db = IDBPDatabase<unknown>;
let dbPromise: Promise<Db> | null = null;

function db(): Promise<Db> {
  if (!dbPromise) {
    dbPromise = openDB(OUTBOX_DB_NAME, OUTBOX_DB_VERSION, {
      upgrade(database, oldVersion) {
        if (oldVersion < 1) {
          const store = database.createObjectStore(OUTBOX_STORE, { keyPath: 'photo_id' });
          store.createIndex('owner', 'owner_user_id');
          store.createIndex('queued_at', 'queued_at');
          database.createObjectStore(META_STORE, { keyPath: 'key' });
        }
      },
      blocked() {
        console.warn('[outbox] database upgrade blocked by another tab');
      },
    });
    dbPromise.catch(() => {
      dbPromise = null; // let the next call retry (e.g. private mode that later allows storage)
    });
  }
  return dbPromise;
}

export const idbOutboxStore: OutboxStore = {
  async put(item) {
    await (await db()).put(OUTBOX_STORE, item);
  },
  async get(photoId) {
    return (await (await db()).get(OUTBOX_STORE, photoId)) as OutboxItem | undefined;
  },
  async delete(photoId) {
    await (await db()).delete(OUTBOX_STORE, photoId);
  },
  async all() {
    return ((await (await db()).getAllFromIndex(OUTBOX_STORE, 'queued_at')) as OutboxItem[]) ?? [];
  },
  async update(photoId, patch) {
    const tx = (await db()).transaction(OUTBOX_STORE, 'readwrite');
    const cur = (await tx.store.get(photoId)) as OutboxItem | undefined;
    if (!cur) {
      await tx.done;
      return undefined;
    }
    const next = { ...cur, ...patch, photo_id: cur.photo_id, owner_user_id: cur.owner_user_id };
    await tx.store.put(next);
    await tx.done;
    return next;
  },
  async acquireLock(holder, ttlMs, nowMs = Date.now()) {
    const tx = (await db()).transaction(META_STORE, 'readwrite');
    const rec = (await tx.store.get(LOCK_KEY)) as ({ key: string } & RunnerLock) | undefined;
    if (rec && rec.holder !== holder && rec.expires_at > nowMs) {
      await tx.done;
      return false;
    }
    await tx.store.put({ key: LOCK_KEY, holder, expires_at: nowMs + ttlMs });
    await tx.done;
    return true;
  },
  async releaseLock(holder) {
    const tx = (await db()).transaction(META_STORE, 'readwrite');
    const rec = (await tx.store.get(LOCK_KEY)) as ({ key: string } & RunnerLock) | undefined;
    if (rec && rec.holder === holder) await tx.store.delete(LOCK_KEY);
    await tx.done;
  },
  async getMeta<T>(key: string) {
    const rec = (await (await db()).get(META_STORE, key)) as { key: string; value: T } | undefined;
    return rec?.value;
  },
  async setMeta<T>(key: string, value: T) {
    await (await db()).put(META_STORE, { key, value });
  },
};

/** An in-memory store with the same contract, for unit tests (and as a no-IndexedDB fallback is NOT used: see isOutboxAvailable). */
export function createMemoryOutboxStore(): OutboxStore & { items: Map<string, OutboxItem>; lock: RunnerLock | null } {
  const items = new Map<string, OutboxItem>();
  const meta = new Map<string, unknown>();
  const self = {
    items,
    lock: null as RunnerLock | null,
    async put(item: OutboxItem) { items.set(item.photo_id, { ...item }); },
    async get(id: string) { const v = items.get(id); return v ? { ...v } : undefined; },
    async delete(id: string) { items.delete(id); },
    async all() { return [...items.values()].map((v) => ({ ...v })).sort((a, b) => a.queued_at.localeCompare(b.queued_at)); },
    async update(id: string, patch: Partial<OutboxItem>) {
      const cur = items.get(id);
      if (!cur) return undefined;
      const next = { ...cur, ...patch, photo_id: cur.photo_id, owner_user_id: cur.owner_user_id };
      items.set(id, next);
      return { ...next };
    },
    async acquireLock(holder: string, ttlMs: number, nowMs = Date.now()) {
      if (self.lock && self.lock.holder !== holder && self.lock.expires_at > nowMs) return false;
      self.lock = { holder, expires_at: nowMs + ttlMs };
      return true;
    },
    async releaseLock(holder: string) { if (self.lock?.holder === holder) self.lock = null; },
    async getMeta<T>(key: string) { return meta.get(key) as T | undefined; },
    async setMeta<T>(key: string, value: T) { meta.set(key, value); },
  };
  return self;
}

/** IndexedDB usable here? (Blocked in some private modes; then the app keeps today's send-now behaviour.) */
export async function isOutboxAvailable(): Promise<boolean> {
  if (typeof indexedDB === 'undefined') return false;
  try {
    await db();
    return true;
  } catch {
    return false;
  }
}
